<template>
    <div class="mx-auto max-w-[600px] px-3 pt-6 sm:px-6 sm:pt-10">
        <!-- Page Header -->
        <div class="flex items-center gap-2 mb-16">
            <Settings class="size-6 text-new-neutral-800" />
            <h1 class="text-3xl text-new-neutral-800">Settings</h1>
        </div>

        <!-- Profile Header -->
        <div class="flex items-center gap-4 mb-9">
            <div class="flex size-[100px] items-center justify-center rounded-full border border-new-neutral-300 bg-white">
                <img
                    v-if="user.profile_photo_path"
                    :src="user.profile_photo_path"
                    class="size-[100px] rounded-full object-cover"
                />
                <span v-else class="text-3xl font-semibold text-new-neutral-400">
                    {{ initials }}
                </span>
            </div>
            <div class="space-y-1.5">
                <p class="text-xl font-semibold text-foreground">{{ user.name }}</p>
                <p class="text-sm text-muted-foreground">{{ user.email }}</p>
            </div>
        </div>

        <!-- Tabs -->
        <nav
            class="mb-8 flex flex-wrap items-center gap-1 border-b border-border"
            aria-label="Settings sections"
        >
            <button
                v-for="tab in tabs"
                :key="tab.key"
                type="button"
                class="flex min-h-10 shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm whitespace-nowrap transition-colors"
                :class="
                    activeTab === tab.key
                        ? 'border-new-topol-blue bg-surface-card text-foreground dark:bg-muted'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                "
                @click="navigate(tab)"
            >
                {{ tab.label }}
            </button>
        </nav>

        <div v-if="activeTab === 'profile'" class="mt-4 space-y-4 pb-12">
            <IncomingInvitationsCard :invitations="invitations" />
            <PersonalInfoCard :has-password="hasPassword" />
            <PasswordCard :has-password="hasPassword" />
            <TwoFactorCard :has-password="hasPassword" />
            <BrowserSessionsCard :sessions="sessions" :has-password="hasPassword" />
            <AppTypeCard />
        </div>

        <div v-else-if="activeTab === 'api-tokens'" class="mt-4 pb-12">
            <ApiTokensCard :tokens="tokens" />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, PropType } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { Settings } from 'lucide-vue-next';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import ISession from '@/types/Settings/ISession';
import IInvitation from '@/types/Members/IInvitation';
import DefaultLayoutV2 from '@/Layouts/DefaultV2.vue';
import PersonalInfoCard from './components/PersonalInfoCard.vue';
import PasswordCard from './components/PasswordCard.vue';
import TwoFactorCard from './components/TwoFactorCard.vue';
import BrowserSessionsCard from './components/BrowserSessionsCard.vue';
import AppTypeCard from './components/AppTypeCard.vue';
import ApiTokensCard from './components/ApiTokensCard.vue';
import IncomingInvitationsCard from './components/IncomingInvitationsCard.vue';

interface IApiToken {
    id: number;
    name: string;
    abilities: string[];
}

defineOptions({
    layout: DefaultLayoutV2,
});

type SettingsTab = 'profile' | 'api-tokens';

const props = defineProps({
    sessions: {
        type: Array as PropType<Array<ISession>>,
        required: true,
    },
    has_password: {
        type: Boolean,
        required: true,
    },
    tokens: {
        type: Array as PropType<IApiToken[]>,
        default: () => [],
    },
    invitations: {
        type: Array as PropType<IInvitation[]>,
        default: () => [],
    },
    initial_tab: {
        type: String as PropType<SettingsTab>,
        default: 'profile',
    },
});

const globalProps = computed(() => usePage<ITopolPageProps>().props);
const user = computed(() => globalProps.value.user);
const hasPassword = computed(() => props.has_password);
const activeTab = ref<SettingsTab>(props.initial_tab);

const tabs: Array<{ key: SettingsTab; label: string; routeName: string }> = [
    { key: 'profile', label: 'Profile', routeName: 'settings.profile-settings.index' },
    { key: 'api-tokens', label: 'API tokens', routeName: 'settings.api-tokens.index' },
];

// Navigate only — the destination route re-renders this page with its own
// `initial_tab`, which drives `activeTab`. Switching `activeTab` here instead
// would flip the visible content before the navigation runs, causing the
// flash/jump (most visible when a pending invitation card re-lays out).
const navigate = (tab: { key: SettingsTab; routeName: string }): void => {
    if (tab.key === activeTab.value) return;
    router.get(route(tab.routeName), {}, { preserveScroll: true });
};

const initials = computed(() => {
    const parts = user.value.name.split(' ');
    return parts.map((p: string) => p[0]?.toUpperCase()).join('').slice(0, 2);
});
</script>
